import React from 'react';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import ClearIcon from '@mui/icons-material/Clear';

import { useCurrentTracklog, useCurrentLogbook } from 'ff/debrief/hooks';
import {
    useAddLogbookObjectIdMutation,
    useDeleteLogbookEntryAssociationMutation,
} from 'ff/debrief/features/TracklogApi';
import {
    FFCDLogbookEntry,
    TracklogLogbookAssociation,
} from 'ff/debrief/models';
import LogbookEntryDetails from 'ff/debrief/components/logbookAssociation/LogbookEntryDetails';
import LogbookEntryHeader from 'ff/debrief/components/logbookAssociation/LogbookEntryHeader';
import { LogbookEntry } from 'shared/sharedTypes/logbook/LogbookEntryModels';

interface LogbookEntryListProps {
    entries: (LogbookEntry | FFCDLogbookEntry)[];
    isLoading: boolean;
    emptyMessage: string;
}

const LogbookEntryList: React.FC<LogbookEntryListProps> = ({
    entries,
    isLoading,
    emptyMessage,
}) => {
    const { tracklog } = useCurrentTracklog();
    const { isLogbookAssociated } = useCurrentLogbook();
    const [addLogbookObjectId] = useAddLogbookObjectIdMutation();
    const [deleteLogbookEntryAssociation] =
        useDeleteLogbookEntryAssociationMutation();

    const handleIconButtonClick = (
        logbookEntry: FFCDLogbookEntry | LogbookEntry,
        associatedState: boolean
    ) => {
        const associationLog: TracklogLogbookAssociation = {
            tracklogObjectId: tracklog.objectId,
            logbookEntryObjectId: logbookEntry.objectId,
        };

        if (associatedState) {
            deleteLogbookEntryAssociation(associationLog);
        } else {
            addLogbookObjectId(associationLog);
        }
    };

    const renderLogbookEntry = (
        logbookEntry: FFCDLogbookEntry | LogbookEntry
    ) => {
        const isAssociated = isLogbookAssociated(logbookEntry.objectId);

        return (
            <ListItem
                key={logbookEntry.objectId}
                disablePadding
                sx={{ borderBottom: '1px solid #e7e7e7' }}
            >
                <IconButton
                    sx={{
                        marginLeft: '1px',
                        marginRight: '5px',
                        color: isAssociated ? '#a7b3be' : '#008CC4',
                        '&:hover': {
                            transition: 'color 0.2s ease-in-out',
                            color: '#008CC4',
                        },
                    }}
                    onClick={() =>
                        handleIconButtonClick(logbookEntry, isAssociated)
                    }
                    disableRipple={false}
                >
                    {isAssociated ? <ClearIcon /> : <AddIcon />}
                </IconButton>
                <ListItemText>
                    <Box display="flex" flexDirection="column">
                        <LogbookEntryHeader logbookEntry={logbookEntry} />
                        <LogbookEntryDetails
                            logbookEntry={logbookEntry}
                            isAssociated={isAssociated}
                        />
                    </Box>
                </ListItemText>
            </ListItem>
        );
    };

    if (!Array.isArray(entries) || entries.length === 0) {
        return (
            <Typography
                sx={{
                    color: '#526f8a',
                    textAlign: 'center',
                    fontWeight: '900',
                    padding: '20px',
                }}
            >
                {isLoading ? <CircularProgress size={21} /> : emptyMessage}
            </Typography>
        );
    }

    return <>{entries.map(renderLogbookEntry)}</>;
};

export default LogbookEntryList;
